// Gọi máy chủ cho hộp thư kín (QĐ 0272). Hai luật riêng của mảng này:
//   · Mã tra cứu chỉ đi trong THÂN yêu cầu, và tra cứu là useMutation — kết quả
//     không vào bộ nhớ đệm của react-query (máy ở quầy dùng chung).
//   · Lời máy chủ hiện nguyên văn qua serverErrorMessage (Luật 6).
import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiBaseUrl } from "@/lib/api/client";
import { postFormAuthed } from "@/lib/api/authed-fetch";
import { serverErrorMessage } from "@/lib/api/error-message";
import type { components } from "@/lib/api/generated/schema";
import { ACTION } from "@/lib/auth/actions";
import { hasPermission } from "@/lib/authv2/permissions";
import { SEALED_INBOX_OFF, useSealedInboxFlag } from "./flag";

type S = components["schemas"];
export type SealedMe = S["SealedInboxMe"];
export type SubmitInput = S["SealedInboxSubmitInput"];
export type MineItem = S["SealedInboxMineItem"];
export type MineDetail = S["SealedInboxMineDetail"];
export type ReaderItem = S["SealedInboxReaderItem"];
export type ReaderDetail = S["SealedInboxReaderDetail"];
export type SealedReader = S["SealedInboxReader"];
export type SealedMessage = S["SealedInboxMessage"];
export type SealedAttachment = S["SealedInboxAttachment"];
export type SealedReward = S["SealedInboxReward"];
export type VerdictInput = S["SealedInboxVerdictInput"];
export type RewardInput = S["SealedInboxRewardInput"];
export type PaidInput = S["SealedInboxPaidInput"];
export type InboxFilter = { kind?: ReaderItem["kind"] | undefined; status?: ReaderItem["status"] | undefined };

export const sealedKeys = {
  all: ["sealed-inbox"] as const,
  me: ["sealed-inbox", "me"] as const,
  mine: ["sealed-inbox", "mine"] as const,
  mineDetail: (id: string) => ["sealed-inbox", "mine", id] as const,
  inbox: (f: InboxFilter) => ["sealed-inbox", "inbox", f.kind ?? "", f.status ?? ""] as const,
  inboxAll: ["sealed-inbox", "inbox"] as const,
  reader: (id: string) => ["sealed-inbox", "reader", id] as const,
  readers: ["sealed-inbox", "readers"] as const,
};

export function useSealedInboxMe(enabled: boolean) {
  return useQuery({
    queryKey: sealedKeys.me,
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/sealed-inbox/me");
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được hộp thư kín"));
      return data;
    },
  });
}

/** Nạp trạng thái hộp thư kín cho menu (cổng + viên đỏ). */
export function useSealedInboxMeSync(loggedIn: boolean) {
  const allowed =
    loggedIn && (hasPermission(ACTION.SealedInboxSubmit) || hasPermission(ACTION.SealedInboxRead));
  const q = useSealedInboxMe(allowed);
  const set = useSealedInboxFlag((s) => s.set);
  const d = allowed ? q.data : undefined;
  useEffect(() => {
    set(
      d
        ? {
            enabled: d.enabled,
            isReader: d.isReader,
            seesSender: d.seesSender,
            senderUnread: d.senderUnread,
            readerUnread: d.readerUnread,
          }
        : SEALED_INBOX_OFF,
    );
  }, [d, set]);
}

// ── Người gửi ──────────────────────────────────────────────────────────────

export function useMine(enabled = true) {
  return useQuery({
    queryKey: sealedKeys.mine,
    enabled,
    queryFn: async () => {
      const { data, error } = await api.GET("/sealed-inbox/mine");
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được thư của bạn"));
      return data;
    },
  });
}

export function useMineDetail(id: string) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: sealedKeys.mineDetail(id),
    queryFn: async () => {
      const { data, error } = await api.GET("/sealed-inbox/mine/{id}", { params: { path: { id } } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không mở được thư"));
      // Mở thư là xoá dấu "có phản hồi mới" — viên đỏ trên menu phải theo.
      void qc.invalidateQueries({ queryKey: sealedKeys.me });
      void qc.invalidateQueries({ queryKey: sealedKeys.mine });
      return data;
    },
  });
}

export function useSubmitReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: SubmitInput) => {
      const { data, error } = await api.POST("/sealed-inbox/reports", { body });
      if (error || !data) throw new Error(serverErrorMessage(error, "Chưa gửi được thư"));
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: sealedKeys.mine }),
  });
}

export function useMineAddMessage(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { id: string; body: string }) => {
      const { data, error } = await api.POST("/sealed-inbox/mine/{id}/messages", {
        params: { path: { id } },
        body,
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Chưa gửi được phần bổ sung"));
      return data;
    },
    onSuccess: (d) => qc.setQueryData(sealedKeys.mineDetail(id), d),
  });
}

/** Tra cứu thư ẩn danh. useMutation — kết quả KHÔNG vào bộ nhớ đệm. */
export function useLookup() {
  return useMutation({
    mutationFn: async (code: string) => {
      const { data, error } = await api.POST("/sealed-inbox/lookup", { body: { code } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không mở được thư"));
      return data;
    },
  });
}

export function useLookupAddMessage() {
  return useMutation({
    mutationFn: async (body: { code: string; id: string; body: string }) => {
      const { data, error } = await api.POST("/sealed-inbox/lookup/messages", { body });
      if (error || !data) throw new Error(serverErrorMessage(error, "Chưa gửi được phần bổ sung"));
      return data;
    },
  });
}

/** Nơi gắn ảnh: thư kín danh theo mã thư, thư ẩn danh theo mã tra cứu. */
export type PhotoTarget = { kind: "mine"; id: string } | { kind: "lookup"; code: string };

/** Tải một ảnh bằng chứng lên; trả bản thư mới nhất. */
export async function uploadEvidence(target: PhotoTarget, file: File): Promise<MineDetail> {
  const form = new FormData();
  form.append("file", file);
  let url: string;
  if (target.kind === "mine") {
    url = `${apiBaseUrl}/sealed-inbox/mine/${encodeURIComponent(target.id)}/attachments`;
  } else {
    url = `${apiBaseUrl}/sealed-inbox/lookup/attachments`;
    form.append("code", target.code);
  }
  const res = await postFormAuthed(url, form);
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) throw new Error(serverErrorMessage(body, `Chưa tải được ảnh "${file.name}"`));
  return body as MineDetail;
}

/** Đọc một ảnh bằng chứng đã giải mã (cửa đòi đăng nhập nên không dùng <img src> thẳng). */
export async function fetchEvidence(attachmentId: string, code?: string): Promise<Blob> {
  if (code) {
    const { data, error } = await api.POST("/sealed-inbox/lookup/attachment-file", {
      body: { code, attachmentId },
      parseAs: "blob",
    });
    if (error || !data) throw new Error(serverErrorMessage(error, "Không mở được ảnh"));
    return data;
  }
  const { data, error } = await api.GET("/sealed-inbox/attachments/{aid}", {
    params: { path: { aid: attachmentId } },
    parseAs: "blob",
  });
  if (error || !data) throw new Error(serverErrorMessage(error, "Không mở được ảnh"));
  return data;
}

/** Danh bạ cho ô "Người bị tố": mọi nhân sự, chỉ mã/tên/ảnh (cùng khoá với danh bạ ảnh đại diện). */
export function useStaffDirectory() {
  return useQuery({
    queryKey: ["staff", "avatars"],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/staff/avatars", {});
      if (error || !Array.isArray(data)) throw new Error(serverErrorMessage(error, "Không tải được danh bạ nhân sự"));
      return data;
    },
  });
}

// ── Người đọc ──────────────────────────────────────────────────────────────

export function useInbox(filter: InboxFilter) {
  return useQuery({
    queryKey: sealedKeys.inbox(filter),
    queryFn: async () => {
      const query: { kind?: ReaderItem["kind"]; status?: ReaderItem["status"] } = {};
      if (filter.kind) query.kind = filter.kind;
      if (filter.status) query.status = filter.status;
      const { data, error } = await api.GET("/sealed-inbox/reports", { params: { query } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được hộp thư kín"));
      return data;
    },
  });
}

export function useReaderDetail(id: string) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: sealedKeys.reader(id),
    queryFn: async () => {
      const { data, error } = await api.GET("/sealed-inbox/reports/{id}", { params: { path: { id } } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không mở được thư"));
      void qc.invalidateQueries({ queryKey: sealedKeys.me });
      void qc.invalidateQueries({ queryKey: sealedKeys.inboxAll });
      return data;
    },
  });
}

function useReaderWrite<T>(id: string, fallback: string, call: (body: T) => Promise<{ data?: ReaderDetail; error?: unknown }>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: T) => {
      const { data, error } = await call(body);
      if (error || !data) throw new Error(serverErrorMessage(error, fallback));
      return data;
    },
    onSuccess: (d) => {
      qc.setQueryData(sealedKeys.reader(id), d);
      void qc.invalidateQueries({ queryKey: sealedKeys.inboxAll });
    },
  });
}

export function useReaderAddMessage(id: string) {
  return useReaderWrite<{ id: string; body: string }>(id, "Chưa gửi được câu hỏi", (body) =>
    api.POST("/sealed-inbox/reports/{id}/messages", { params: { path: { id } }, body }),
  );
}

export function useSetVerdict(id: string) {
  return useReaderWrite<VerdictInput>(id, "Chưa lưu được kết luận", (body) =>
    api.POST("/sealed-inbox/reports/{id}/verdict", { params: { path: { id } }, body }),
  );
}

export function useReward(id: string) {
  return useReaderWrite<RewardInput>(id, "Chưa lưu được quyết định thưởng", (body) =>
    api.POST("/sealed-inbox/reports/{id}/reward", { params: { path: { id } }, body }),
  );
}

export function useMarkPaid(id: string) {
  return useReaderWrite<PaidInput>(id, "Chưa ghi được khoản đã trả", (body) =>
    api.POST("/sealed-inbox/reports/{id}/reward/paid", { params: { path: { id } }, body }),
  );
}

export function useReaders(enabled: boolean) {
  return useQuery({
    queryKey: sealedKeys.readers,
    enabled,
    queryFn: async () => {
      const { data, error } = await api.GET("/sealed-inbox/readers");
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được danh sách người đọc"));
      return data;
    },
  });
}

export function useAddReader() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (staffId: string) => {
      const { data, error } = await api.POST("/sealed-inbox/readers", { body: { staffId } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Chưa thêm được người đọc"));
      return data;
    },
    onSuccess: (d) => qc.setQueryData(sealedKeys.readers, d),
  });
}

export function useRemoveReader() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (staffId: string) => {
      const { data, error } = await api.DELETE("/sealed-inbox/readers/{staffId}", {
        params: { path: { staffId } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Chưa gỡ được người đọc"));
      return data;
    },
    onSuccess: (d) => qc.setQueryData(sealedKeys.readers, d),
  });
}
